// src/pages/Admin.js
import React, { useEffect, useState } from "react";
import api from "../api";
import ListEditor from "../components/ListEditor";
import ImageCropUpload from "../components/ImageCropUpload";
import BookingRequestsAdmin from "../components/BookingRequestsAdmin";
import "../styles/Admin.css";

// If your backend DOESN'T implement /api/hero yet, keep this false.
// When you add the backend route later, set REACT_APP_ENABLE_HERO_API=true and rebuild.
const HERO_API_ENABLED =
  String(process.env.REACT_APP_ENABLE_HERO_API || "").toLowerCase() === "true" ||
  String(process.env.REACT_APP_ENABLE_HERO_API || "") === "1";

export default function Admin() {
  const [tab, setTab] = useState("aboutus");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(localStorage.getItem("pccm_admin_token") || "");
  const [error, setError] = useState("");

  // content states
  const [aboutus, setAboutus] = useState({ boardOfDirectors: [], boardMembers: [] });
  const [ads, setAds] = useState({ ads: [] });
  const [events, setEvents] = useState({ programs: [] });
  const [finance, setFinance] = useState({ currentReports: [], pastReports: [] });
  const [gallery, setGallery] = useState({ images: [] });
  const [heroImage, setHeroImage] = useState({ url: "" });
  const [metrics, setMetrics] = useState({ stats: [] });

  const authed = !!token;

  const logout = () => {
    localStorage.removeItem("pccm_admin_token");
    setToken("");
    setError("");
  };

  const loadAll = async () => {
    try {
      // ✅ FIX: pass token for endpoints that are protected
      const [a, d, e, f, g, m] = await Promise.all([
        api("/api/aboutus", { token }),
        api("/api/ads", { token }),
        api("/api/events", { token }),
        api("/api/finance", { token }),
        api("/api/gallery", { token }),
        api("/api/metrics", { token }),
      ]);

      setAboutus(a);
      setAds(d);
      setEvents(e);
      setFinance(f);
      setGallery(g);
      setMetrics(m);
    } catch (e) {
      if (e?.status === 401) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }
      console.error("Error loading all data:", e);
      setError(e.message || "Failed to load data");
    }
  };

  useEffect(() => {
    if (authed) loadAll().catch((e) => setError(e.message));
    // eslint-disable-next-line
  }, [authed]);

  // ✅ FIX: only hit /api/hero when enabled AND user is on hero tab
  useEffect(() => {
    if (!authed) return;
    if (!HERO_API_ENABLED) return;
    if (tab !== "hero") return;

    (async () => {
      try {
        const h = await api("/api/hero", { token });
        if (h && typeof h === "object" && "url" in h) setHeroImage(h);
      } catch (e) {
        if (e?.status === 401) {
          setError("Session expired. Please log in again.");
          logout();
          return;
        }
        // If route is missing, keep quiet (but ideally you enable it only when backend exists)
        if (e?.status !== 404) setError(e.message || "Failed to load hero image");
      }
    })();
    // eslint-disable-next-line
  }, [authed, tab]);

  const login = async () => {
    setError("");
    try {
      const res = await api("/api/auth/login", {
        method: "POST",
        body: { password },
      });
      localStorage.setItem("pccm_admin_token", res.token);
      setToken(res.token);
      setPassword("");
    } catch (e) {
      setError(e.message);
    }
  };

  const saveAboutUs = async () => {
    setError("");
    try {
      await api("/api/aboutus", { method: "PUT", body: aboutus, token });
      alert("Saved About Us!");
    } catch (e) {
      if (e?.status === 401) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }
      setError(e.message);
    }
  };

  const saveAds = async () => {
    setError("");
    try {
      await api("/api/ads", { method: "PUT", body: ads, token });
      alert("Saved Ads!");
    } catch (e) {
      if (e?.status === 401) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }
      setError(e.message);
    }
  };

  const saveEvents = async () => {
    setError("");
    try {
      await api("/api/events", { method: "PUT", body: events, token });
      alert("Saved Events!");
    } catch (e) {
      if (e?.status === 401) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }
      setError(e.message);
    }
  };

  const saveFinance = async () => {
    setError("");
    try {
      await api("/api/finance", { method: "PUT", body: finance, token });
      alert("Saved Finance!");
    } catch (e) {
      if (e?.status === 401) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }
      setError(e.message);
    }
  };

  const saveGallery = async () => {
    setError("");
    try {
      await api("/api/gallery", { method: "PUT", body: gallery, token });
      alert("Saved Gallery!");
    } catch (e) {
      if (e?.status === 401) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }
      setError(e.message);
    }
  };

  const saveHeroImage = async () => {
    setError("");
    try {
      await api("/api/hero", { method: "PUT", body: heroImage, token });
      alert("Saved Hero Image!");
    } catch (e) {
      if (e?.status === 401) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }
      setError(e.message);
    }
  };

  const saveMetrics = async () => {
    setError("");
    try {
      await api("/api/metrics", { method: "PUT", body: metrics, token });
      alert("Saved Metrics!");
    } catch (e) {
      if (e?.status === 401) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }
      setError(e.message);
    }
  };

  const renderContent = () => {
    if (tab === "aboutus") {
      return (
        <>
          <ListEditor
            title="Board of Directors (Contact Cards)"
            items={aboutus.boardOfDirectors}
            onChange={(list) => setAboutus({ ...aboutus, boardOfDirectors: list })}
            token={token}
            onAuthError={logout}
            defaultItem={{ name: "", position: "", description: "", image: "" }}
            fields={[
              { key: "name", label: "Name" },
              { key: "position", label: "Position" },
              { key: "image", label: "Headshot", type: "image", folder: "boardmembers", aspect: 1 },
              { key: "description", label: "Description", type: "textarea", maxLength: 200 },
            ]}
          />
          <div style={{ height: 16 }} />
          <ListEditor
            title="Board Members (Contact Cards)"
            items={aboutus.boardMembers}
            onChange={(list) => setAboutus({ ...aboutus, boardMembers: list })}
            token={token}
            onAuthError={logout}
            defaultItem={{ name: "", description: "", image: "" }}
            fields={[
              { key: "name", label: "Name" },
              { key: "image", label: "Headshot", type: "image", folder: "boardmembers", aspect: 1 },
              { key: "description", label: "Description", type: "textarea", maxLength: 200 },
            ]}
          />
          <div className="admin-toolbar">
            <button type="button" className="admin-btn-primary" onClick={saveAboutUs}>
              Save About Us
            </button>
          </div>
        </>
      );
    }

    if (tab === "ads") {
      return (
        <>
          <ListEditor
            title="Promotions / Ads"
            items={ads.ads}
            onChange={(list) => setAds({ ads: list })}
            token={token}
            onAuthError={logout}
            defaultItem={{ title: "", description: "", image: "", link: "" }}
            fields={[
              { key: "title", label: "Title" },
              { key: "link", label: "Link URL", placeholder: "https://..." },
              { key: "image", label: "Banner Image", type: "image", folder: "ads", aspect: 3 / 1 },
              { key: "description", label: "Description", type: "textarea", maxLength: 300 },
            ]}
          />
          <div className="admin-toolbar">
            <button type="button" className="admin-btn-primary" onClick={saveAds}>
              Save Ads
            </button>
          </div>
        </>
      );
    }

    if (tab === "metrics") {
      return (
        <>
          <ListEditor
            title="Homepage Metrics (Hero Counters)"
            items={metrics.stats || []}
            onChange={(list) => setMetrics({ stats: list })}
            token={token}
            onAuthError={logout}
            defaultItem={{ label: "", value: "" }}
            fields={[
              { key: "value", label: "Number/Value" },
              { key: "label", label: "Label" },
            ]}
          />
          <div className="admin-toolbar">
            <button type="button" className="admin-btn-primary" onClick={saveMetrics}>
              Save Metrics
            </button>
          </div>
        </>
      );
    }

    if (tab === "events") {
      return (
        <>
          <ListEditor
            title="Programs & Services (Events page cards)"
            items={events.programs}
            onChange={(list) => setEvents({ programs: list })}
            token={token}
            onAuthError={logout}
            defaultItem={{ title: "", description: "", image: "", link: "" }}
            fields={[
              { key: "title", label: "Title" },
              { key: "link", label: "Link (internal or external)", placeholder: "/events#something or https://..." },
              { key: "image", label: "Card Image", type: "image", folder: "programs", aspect: 16 / 9 },
              { key: "description", label: "Description", type: "textarea", maxLength: 250 },
            ]}
          />
          <div className="admin-toolbar">
            <button type="button" className="admin-btn-primary" onClick={saveEvents}>
              Save Events
            </button>
          </div>
        </>
      );
    }

    if (tab === "finance") {
      return (
        <>
          <ListEditor
            title="Current Reports (Annual / Income / Balance)"
            items={finance.currentReports}
            onChange={(list) => setFinance({ ...finance, currentReports: list })}
            token={token}
            onAuthError={logout}
            defaultItem={{ type: "annualReport", fiscalyear: "", url: "" }}
            fields={[
              {
                key: "type",
                label: "Type",
                type: "select",
                options: [
                  { value: "annualReport", label: "Annual Report" },
                  { value: "incomeStatement", label: "Income Statement" },
                  { value: "balanceSheet", label: "Balance Sheet" },
                ],
              },
              { key: "fiscalyear", label: "Fiscal Year (e.g. 2024-2025)" },
              { key: "url", label: "PDF Document", type: "file", folder: "reports" },
            ]}
          />
          <div style={{ height: 16 }} />
          <ListEditor
            title="Past Reports & Statements (PDF Uploads)"
            items={finance.pastReports}
            onChange={(list) => setFinance({ ...finance, pastReports: list })}
            token={token}
            onAuthError={logout}
            defaultItem={{ title: "", url: "" }}
            fields={[
              { key: "title", label: "Title" },
              { key: "url", label: "PDF Document", type: "file", folder: "reports" },
            ]}
          />
          <div className="admin-toolbar">
            <button type="button" className="admin-btn-primary" onClick={saveFinance}>
              Save Finance
            </button>
          </div>
        </>
      );
    }

    if (tab === "gallery") {
      return (
        <>
          <ListEditor
            title="Gallery Images"
            items={gallery.images}
            onChange={(list) => setGallery({ images: list })}
            token={token}
            onAuthError={logout}
            defaultItem={{ url: "", caption: "" }}
            fields={[
              { key: "url", label: "Gallery Image", type: "image", folder: "gallery", aspect: 16 / 9 },
              { key: "caption", label: "Caption", type: "textarea", maxLength: 150 },
            ]}
          />
          <div className="admin-toolbar">
            <button type="button" className="admin-btn-primary" onClick={saveGallery}>
              Save Gallery
            </button>
          </div>
        </>
      );
    }

    if (tab === "bookings") {
      return <BookingRequestsAdmin token={token} onAuthError={logout} />;
    }

    return (
      <div className="admin-card">
        <h2 style={{ marginTop: 0 }}>Website Hero Image</h2>
        <ImageCropUpload
          token={token}
          onAuthError={logout}
          value={heroImage.url}
          onChange={(url) => setHeroImage({ url })}
          folder="general"
          label="Hero Background"
          aspect={3 / 1}
        />
        <div className="admin-toolbar">
          <button type="button" className="admin-btn-primary" onClick={saveHeroImage}>
            Save Hero Image
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-wrap">
      <h1 style={{ marginTop: 0 }}>PCCM Content Admin</h1>

      {!authed ? (
        <div className="admin-card">
          <h2 style={{ marginTop: 0 }}>Login</h2>

          <div style={{ maxWidth: 420 }}>
            <label style={{ fontWeight: 700, display: "block", marginBottom: 8 }}>
              Admin Password
            </label>
            <input
              className="admin-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              onKeyDown={(e) => {
                if (e.key === "Enter") login();
              }}
              style={{ display: "block", fontSize: 18, padding: "12px 14px" }}
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <button type="button" className="admin-btn-primary" onClick={login}>
              Login
            </button>
          </div>

          {error ? <p style={{ color: "crimson", marginTop: 10 }}>{error}</p> : null}
        </div>
      ) : (
        <>
          <div className="admin-toolbar">
            <div className="admin-tabs">
              <button
                type="button"
                className={`admin-tab ${tab === "aboutus" ? "active" : ""}`}
                onClick={() => setTab("aboutus")}
              >
                About Us Cards
              </button>
              <button
                type="button"
                className={`admin-tab ${tab === "ads" ? "active" : ""}`}
                onClick={() => setTab("ads")}
              >
                Ads Banner
              </button>
              <button
                type="button"
                className={`admin-tab ${tab === "metrics" ? "active" : ""}`}
                onClick={() => setTab("metrics")}
              >
                Site Metrics
              </button>
              <button
                type="button"
                className={`admin-tab ${tab === "events" ? "active" : ""}`}
                onClick={() => setTab("events")}
              >
                Events Cards
              </button>
              <button
                type="button"
                className={`admin-tab ${tab === "finance" ? "active" : ""}`}
                onClick={() => setTab("finance")}
              >
                Finance Links
              </button>
              <button
                type="button"
                className={`admin-tab ${tab === "gallery" ? "active" : ""}`}
                onClick={() => setTab("gallery")}
              >
                Gallery Images
              </button>
              <button
                type="button"
                className={`admin-tab ${tab === "bookings" ? "active" : ""}`}
                onClick={() => setTab("bookings")}
              >
                Booking Requests
              </button>

              {HERO_API_ENABLED ? (
                <button
                  type="button"
                  className={`admin-tab ${tab === "hero" ? "active" : ""}`}
                  onClick={() => setTab("hero")}
                >
                  Hero Image
                </button>
              ) : null}
            </div>

            <div className="admin-actions">
              <button type="button" className="admin-btn-secondary" onClick={loadAll}>
                Reload
              </button>
              <button type="button" className="admin-btn-secondary" onClick={logout}>
                Logout
              </button>
            </div>
          </div>

          {error ? <p style={{ color: "crimson" }}>{error}</p> : null}
          {renderContent()}
        </>
      )}
    </div>
  );
}
